import { AppType } from '@features/contexts/AppProvider';
import {
  BaseEventStyleKey,
  LogItemStyleKey,
  RadiometerLabelSize,
  StatusChangeLabelSize,
} from '@features/components/ClinicalData/components/ClinicalLog/components/constants';
import { ClinicalLogStyleMap, LogTypeKeys } from '@features/components/ClinicalData/components/ClinicalLog/types';
import { ClinicalItemType, HeartPassOptionType } from '@/api/apis/case';

export const heartPassOptionLabels = {
  [HeartPassOptionType.BeatingHeart]: 'Beating heart',
  [HeartPassOptionType.CoolingAndFlush]: 'Cooling and flush',
};

export const sharedExternallyEvents = [
  ClinicalItemType.ScheduledDonorORStartTime,
  ClinicalItemType.DonorInOR,
  ClinicalItemType.RecordDelay,
  ClinicalItemType.VisualInspection,
  ClinicalItemType.Heparin,
  ClinicalItemType.CrossClampTimeFlushTime,
  ClinicalItemType.OrganAccepted,
  ClinicalItemType.DonorDidNotExpireInTime,
  ClinicalItemType.DryRun,
  ClinicalItemType.NRPStart,
  ClinicalItemType.VitalUpdates,
  ClinicalItemType.DeathDeclarationTime,
  ClinicalItemType.OnSiteCoordinatorsContacts,
  ClinicalItemType.PostFlushBronch,
  ClinicalItemType.PostFlushLungsPhoto,
  ClinicalItemType.BronchInSitu,
  ClinicalItemType.CardiacArrest,
  ClinicalItemType.DonorBiopsyReport,
  ClinicalItemType.LiverOnABackBench,
  ClinicalItemType.LiverAnatomyQuestionnaire,
  ClinicalItemType.AnatomyPhotos,
  ClinicalItemType.LiverReconstructionQuestionnaire,
  ClinicalItemType.ReconstructionVideosPhotos,
  ClinicalItemType.PFOValveCondition,
  ClinicalItemType.InSituVideoOfHeart,
  ClinicalItemType.OrganOnOCS,
  ClinicalItemType.OnOCSVideo,
  ClinicalItemType.LeavingDonorHospital,
  ClinicalItemType.BronchVideoOnTheOCS,
  ClinicalItemType.ArrivalAtRecipientHospital,
  ClinicalItemType.FinalBronchVideoAtTheRecipientHospital,
  ClinicalItemType.FinalPhysicalExamPictureOrVideo,
  ClinicalItemType.OffOCS,
  ClinicalItemType.FinalFlush,
  ClinicalItemType.PerfusionTerminationOption,
  ClinicalItemType.OffOCSPhotoAndVideo,
  ClinicalItemType.WithdrawalOfLifeSupportTime,
  ClinicalItemType.OrganOnSCS,
  ClinicalItemType.OffSCS,
  ClinicalItemType.ScsTerminationOption,
  ClinicalItemType.Randomization,
  ClinicalItemType.OrganApproved,
  ClinicalItemType.OrganDeclined,
  ClinicalItemType.AtDonorHospital,
  ClinicalItemType.SkinIncisionTime,
  ClinicalItemType.BloodCollection,
  ClinicalItemType.PFRatioInSitu,
  ClinicalItemType.PFRatioOnOCSDonorHospital,
  ClinicalItemType.PFRatioOnOCSTXCenter,
  ClinicalItemType.BloodSample,
  ClinicalItemType.PreOCSBloodSample,
  ClinicalItemType.Bile,
];

export const clinicalLogEvents = [
  ClinicalItemType.AtDonorHospital,
  ClinicalItemType.OrganOnOCS,
  ClinicalItemType.LeavingDonorHospital,
  ClinicalItemType.ArrivalAtRecipientHospital,
  ClinicalItemType.OffOCS,
  ClinicalItemType.DonorInOR,
  ClinicalItemType.ScheduledDonorORStartTime,
  ClinicalItemType.Heparin,
  ClinicalItemType.CrossClampTimeFlushTime,
  ClinicalItemType.SkinIncisionTime,
  ClinicalItemType.OrganAccepted,
  ClinicalItemType.NRPStart,
  ClinicalItemType.PreOCSFlush,
  ClinicalItemType.PFOValveCondition,
  ClinicalItemType.Priming,
  ClinicalItemType.FinalFlush,
  ClinicalItemType.LeavingRecipientHospital,
  ClinicalItemType.CardiacArrest,
  ClinicalItemType.DonorDidNotExpireInTime,
  ClinicalItemType.DryRun,
  ClinicalItemType.RecordDelay,
  ClinicalItemType.DonorLabelsCheck,
  ClinicalItemType.BloodAvailabilityCheck,
  ClinicalItemType.BronchCartAndSuctionCheck,
  ClinicalItemType.ReintubationPersonalCheck,
  ClinicalItemType.OnSiteCoordinatorsContacts,
  ClinicalItemType.ResearchBloodSamples,
  ClinicalItemType.TeamHuddle,
  ClinicalItemType.CysticDuct,
  ClinicalItemType.PickUpOfOrganLabelAndTissueSamples,
  ClinicalItemType.IceForTheOCSCoolerPriorDeparture,
  ClinicalItemType.PreOCSBloodSample,
  ClinicalItemType.BloodSample,
  ClinicalItemType.PFRatioInSitu,
  ClinicalItemType.PFRatioOnOCSDonorHospital,
  ClinicalItemType.PFRatioOnOCSTXCenter,
  ClinicalItemType.VisualInspection,
  ClinicalItemType.VitalUpdates,
  ClinicalItemType.PostFlushBronch,
  ClinicalItemType.PostFlushLungsPhoto,
  ClinicalItemType.BronchInSitu,
  ClinicalItemType.LiverOnABackBench,
  ClinicalItemType.AnatomyPhotos,
  ClinicalItemType.ReconstructionVideosPhotos,
  ClinicalItemType.InSituVideoOfHeart,
  ClinicalItemType.Stabilization,
  ClinicalItemType.OnOCSVideo,
  ClinicalItemType.BronchVideoOnTheOCS,
  ClinicalItemType.FinalCheck,
  ClinicalItemType.FinalBronchVideoAtTheRecipientHospital,
  ClinicalItemType.FinalPhysicalExamPictureOrVideo,
  ClinicalItemType.OffOCSPhotoAndVideo,
  ClinicalItemType.DeathDeclarationTime,
  ClinicalItemType.TxOCSBiopsy,
  ClinicalItemType.DonorBiopsyReport,
  ClinicalItemType.LiverAnatomyQuestionnaire,
  ClinicalItemType.LiverReconstructionQuestionnaire,
  ClinicalItemType.BloodCollection,
  ClinicalItemType.ServiceConfirmation,
  ClinicalItemType.DonorSpecimenHandoff,
  ClinicalItemType.MultipleDonorSpecimenHandoff,
  ClinicalItemType.HeartPassOption,
  ClinicalItemType.PerfusionTerminationOption,
  ClinicalItemType.Bile,
  ClinicalItemType.WithdrawalOfLifeSupportTime,
  ClinicalItemType.OrganMonitoring,
  ClinicalItemType.OrganOnSCS,
  ClinicalItemType.OffSCS,
  ClinicalItemType.ScsTerminationOption,
  ClinicalItemType.OrganApproved,
  ClinicalItemType.OrganDeclined,
];

export const lungsTerminationClinicalLogs = [
  ClinicalItemType.PerfusionTerminationOption,
  ClinicalItemType.ScsTerminationOption,
];

export const clinicalLogStyleKeyByPlatform: ClinicalLogStyleMap = {
  [AppType.NopConnectPlatform]: {
    [LogTypeKeys.BaseEventItem]: BaseEventStyleKey.base,
    [LogTypeKeys.LogItemLayout]: LogItemStyleKey.minor,
    [LogTypeKeys.StatusChangeItem]: StatusChangeLabelSize.minor,
    [LogTypeKeys.RadiometerLayout]: RadiometerLabelSize.minor,
  },
  [AppType.NopAccessPlatform]: {
    [LogTypeKeys.BaseEventItem]: BaseEventStyleKey.medium,
    [LogTypeKeys.LogItemLayout]: LogItemStyleKey.base,
    [LogTypeKeys.StatusChangeItem]: StatusChangeLabelSize.base,
    [LogTypeKeys.RadiometerLayout]: RadiometerLabelSize.base,
  },
  [AppType.Failover]: {
    [LogTypeKeys.BaseEventItem]: BaseEventStyleKey.base,
    [LogTypeKeys.LogItemLayout]: LogItemStyleKey.minor,
    [LogTypeKeys.StatusChangeItem]: StatusChangeLabelSize.minor,
    [LogTypeKeys.RadiometerLayout]: RadiometerLabelSize.minor,
  },
};
